// Scale bridge dashboard - net weight recorded today, by hour.
//
// ─── Why bars and not a line ───────────────────────────────────────────
// The measure is a total per hour: a discrete quantity in a labelled bucket, not
// a value sampled along a continuum. A line would draw a slope between 10:00 and
// 11:00 and invite the reader to interpolate a weight at 10:30, which does not
// exist - nothing was weighed then. Bars state each hour's total and claim
// nothing between them.
//
// ─── One measure, one axis ─────────────────────────────────────────────
// Weight and reading-count are both interesting and are on completely different
// scales. They are NOT put on twin y-axes: a dual-axis chart lets the author
// choose where the two series appear to cross, so the reader sees a relationship
// the data does not contain. The count travels in the tooltip instead, where it
// is exact and cannot be misread as a trend.
//
// Single series, so there is no legend - the title names it - and no categorical
// palette to get wrong.

import { Empty } from 'antd';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { BRAND } from '../theme';

export interface HourBucket {
    hour: string;
    kg: number;
    count: number;
}

function ChartTooltip({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ payload: HourBucket }>;
    label?: string;
}) {
    if (!active || !payload?.length) return null;
    const bucket = payload[0].payload;

    return (
        <div
            style={{
                background: '#ffffff',
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                padding: '10px 12px',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                fontSize: 13,
            }}
        >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
            <div className="num">
                {bucket.kg.toLocaleString('en-IN')} kg
            </div>
            <div className="num" style={{ color: BRAND.muted, fontSize: 12 }}>
                {bucket.count} weighing{bucket.count === 1 ? '' : 's'}
            </div>
        </div>
    );
}

export default function WeightByHourChart({ data }: { data: HourBucket[] }) {
    if (data.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No weighings recorded today yet"
                style={{ padding: '40px 0' }}
            />
        );
    }

    return (
        <ResponsiveContainer width="100%" height={260}>
            <BarChart
                data={data}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                // Leaves a surface gap between adjacent bars so each reads as its own
                // mark rather than one continuous block. Belongs on the chart, not the
                // series - it is spacing between categories.
                barCategoryGap="28%"
            >
                {/* Horizontal rules only, and in the border tone: the grid is there to
                    let a bar be read against the axis, not to be looked at. */}
                <CartesianGrid stroke={BRAND.chartGrid} vertical={false} />
                <XAxis
                    dataKey="hour"
                    tick={{ fill: BRAND.chartAxis, fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: BRAND.chartGrid }}
                />
                <YAxis
                    tick={{ fill: BRAND.chartAxis, fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                    unit=" kg"
                />
                <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: 'rgba(29, 78, 216, 0.06)' }}
                />
                <Bar
                    dataKey="kg"
                    fill={BRAND.chartSeries}
                    // Rounded at the data end only, anchored flat to the baseline -
                    // a rounded foot would lift the bar off its own zero line.
                    radius={[4, 4, 0, 0]}
                    maxBarSize={56}
                    // ─── No entry animation, deliberately ────────────────
                    // The dashboard re-polls every few seconds and re-renders
                    // this chart each time. With the default animation on, every
                    // refresh replays the bars growing from zero, which reads as
                    // the numbers changing when nothing has changed - and makes
                    // the chart unreadable for the second it takes to settle.
                    //
                    // It also means the chart is correct the instant it paints,
                    // rather than only after the animation lands; a screenshot of
                    // the animated version caught every bar at zero height.
                    isAnimationActive={false}
                />
            </BarChart>
        </ResponsiveContainer>
    );
}
